import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type {
  FunilVarreduraResumo,
  FunilVarreduraUltima,
  FunilVarreduraItem,
} from '@/types/empresa';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export function useExecutarVarredura(empresaId: string | undefined) {
  return useMutation({
    mutationFn: async () => {
      if (!empresaId) throw new Error('ID da empresa é obrigatório');
      const response = await api.post<ApiResponse<FunilVarreduraResumo>>(
        `/empresas/${empresaId}/funil/varrer`
      );
      return response.data;
    },
  });
}

export function useUltimaVarredura(
  empresaId: string | undefined,
  options: { recomendado?: boolean; page?: number; limit?: number } = {}
) {
  const { recomendado, page = 1, limit = 20 } = options;

  return useQuery<FunilVarreduraUltima | null>({
    queryKey: ['funil-varredura-ultima', empresaId, recomendado, page, limit],
    queryFn: async () => {
      if (!empresaId) throw new Error('ID da empresa é obrigatório');
      const params = new URLSearchParams();
      if (recomendado !== undefined) params.append('recomendado', String(recomendado));
      params.append('page', String(page));
      params.append('limit', String(limit));

      const response = await api.get<ApiResponse<FunilVarreduraUltima | null>>(
        `/empresas/${empresaId}/funil/varreduras/ultima?${params.toString()}`
      );
      return response.data;
    },
    enabled: Boolean(empresaId),
  });
}

export function useDecidirVarreduraItem(empresaId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      itemId,
      decisao,
    }: {
      itemId: number;
      decisao: 'aceito' | 'descartado';
    }) => {
      if (!empresaId) throw new Error('ID da empresa é obrigatório');
      const response = await api.patch<ApiResponse<FunilVarreduraItem>>(
        `/empresas/${empresaId}/funil/varredura-itens/${itemId}`,
        { decisao }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['funil-varredura-ultima', empresaId] });
    },
  });
}
